import { createContext, useCallback, useContext, type ReactNode } from 'react';

// =============================================================================
// 고객사 범위(운영사 > 고객사 관리 > 상세) 안에서 쓰는 조직 id.
// 운영자는 자기 소속 조직(HNINE)이 따로 있어서, 고객사 화면의 모든 /api 호출은 ?orgId=<고객사> 를
// 붙여야 서버가 그 고객사 컨텍스트로 돈다(서버 groupScope 미들웨어가 운영자에게만 이 값을 허용).
// 2026-10-06 대교: 디바이스 상세 모달이 이 값을 안 붙여 403 — 이제 상세 안의 호출은 전부 useScopedUrl() 을 거친다.
// 고객사 범위 밖(내 조직 화면)에서는 아무것도 붙이지 않는다.
// =============================================================================

const ScopeOrgContext = createContext<string | null>(null);

export function ScopeOrgProvider({ orgId, children }: { orgId: string | null; children: ReactNode }): JSX.Element {
  return <ScopeOrgContext.Provider value={orgId}>{children}</ScopeOrgContext.Provider>;
}

export function useScopeOrgId(): string | null { return useContext(ScopeOrgContext); }

/** URL 에 고객사 orgId 를 붙인다. 이미 쿼리가 있으면 & 로, 없으면 ? 로. 범위 밖이면 그대로. */
export function scopedUrl(url: string, orgId: string | null): string {
  if (!orgId || /[?&]orgId=/.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}orgId=${encodeURIComponent(orgId)}`;
}

/** 화면 안 이동(navigate)에 붙일 '?orgId=…'. 범위 밖이면 빈 문자열. 메일·알림 링크(/devices/:id?orgId=)로 들어온 운영자가 수정·캘리브레이션으로 옮겨도 범위를 잃지 않게. */
export function useScopeSearch(): string {
  const orgId = useScopeOrgId();
  return orgId ? `?orgId=${encodeURIComponent(orgId)}` : '';
}

export function useScopedUrl(): (url: string) => string {
  const orgId = useScopeOrgId();
  return useCallback((url: string) => scopedUrl(url, orgId), [orgId]);
}
